import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Printer,
  Calendar,
  Tag,
  CheckSquare,
  Square,
  ZoomIn,
  ZoomOut,
  Sparkles,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import RuahanLabelSticker from './RuahanLabelSticker';

const getIndonesianDate = (d = new Date()) => {
  const months = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
  ];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

export default function CetakRuahanModal({
  isOpen,
  onClose,
  order = {},
}) {
  // Extract brand name
  const rawBrand = useMemo(() => {
    if (order?.brand_name) return order.brand_name.split('/')[0].trim();
    if (order?.client_name) return order.client_name;
    if (order?.nama_brand) return order.nama_brand;
    return 'Brand';
  }, [order]);

  const [brandName, setBrandName] = useState(rawBrand);
  const [tanggal, setTanggal] = useState(getIndonesianDate());
  const [previewZoom, setPreviewZoom] = useState(1.5);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Normalize variants list from order
  const variantList = useMemo(() => {
    const prods = order?.products || [];
    if (prods.length === 0) {
      const rawName = order?.product_name || order?.variant_name || 'Produk Utama';
      const stripped = rawName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
      const match = rawName.match(/\((\d+(?:\.\d+)?\s*(?:ml|l|gr)?)\)/i);
      const sizeDetected = order?.botol_ukuran_aktual || order?.botol_ukuran_label || (match ? match[1] : '30 ml');
      const cleanSize = String(sizeDetected).toLowerCase().includes('ml')
        ? String(sizeDetected)
        : `${sizeDetected} ml`;

      return [{
        id: 'single-prod',
        index: 0,
        rawName,
        variantName: stripped,
        defaultUkuran: cleanSize,
        targetQty: order?.target_qty || 1,
      }];
    }

    return prods.map((p, idx) => {
      const rawName = p.product_name || p.nama_product || p.variant_name || p.nama_produk || p.nama_varian || (p.variant_id ? p.variant_id.replace(/^.*?\s*-\s*/, '') : '') || `Varian ${idx + 1}`;
      const stripped = rawName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
      
      const match = rawName.match(/\((\d+(?:\.\d+)?\s*(?:ml|l|gr)?)\)/i);
      const explicitSize = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || (match ? match[1] : '');
      let cleanSize = '';
      if (explicitSize) {
        cleanSize = String(explicitSize).toLowerCase().includes('ml') ? String(explicitSize) : `${explicitSize} ml`;
      } else if (order?.botol_ukuran_aktual || order?.botol_ukuran_label) {
        const s = order.botol_ukuran_aktual || order.botol_ukuran_label;
        cleanSize = String(s).toLowerCase().includes('ml') ? String(s) : `${s} ml`;
      } else {
        cleanSize = '30 ml';
      }

      return {
        id: p._id || p.id || `var-${idx}`,
        index: idx,
        rawName,
        variantName: stripped,
        defaultUkuran: cleanSize,
        targetQty: p.qty || p.target_qty || 1,
      };
    });
  }, [order]);

  const [selectedIndices, setSelectedIndices] = useState(() => new Set(variantList.map((_, i) => i)));
  const [customUkuranMap, setCustomUkuranMap] = useState({});
  const [copiesMap, setCopiesMap] = useState({});

  useEffect(() => {
    if (isOpen) {
      setBrandName(rawBrand);
      setTanggal(getIndonesianDate());
      setSelectedIndices(new Set(variantList.map((_, i) => i)));
      const sizes = {};
      const copies = {};
      variantList.forEach((v) => {
        sizes[v.index] = v.defaultUkuran;
        copies[v.index] = 1;
      });
      setCustomUkuranMap(sizes);
      setCopiesMap(copies);
    }
  }, [isOpen, rawBrand, variantList]);

  const handleToggleSelect = (index) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIndices.size === variantList.length) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(variantList.map((_, i) => i)));
    }
  };

  const handleUkuranChange = (index, val) => {
    setCustomUkuranMap((prev) => ({ ...prev, [index]: val }));
  };

  const handleCopiesChange = (index, val) => {
    const num = Math.max(1, Math.min(99, parseInt(val, 10) || 1));
    setCopiesMap((prev) => ({ ...prev, [index]: num }));
  };

  // Build the list of stickers
  const stickersToPrint = useMemo(() => {
    const result = [];
    variantList.forEach((v) => {
      if (selectedIndices.has(v.index)) {
        const copies = copiesMap[v.index] || 1;
        const uk = customUkuranMap[v.index] !== undefined ? customUkuranMap[v.index] : v.defaultUkuran;
        for (let i = 0; i < copies; i++) {
          result.push({
            id: `${v.id}-${i}`,
            variantIndex: v.index,
            variantName: v.variantName,
            ukuran: uk,
            brandName,
            tanggal,
          });
        }
      }
    });
    return result;
  }, [variantList, selectedIndices, copiesMap, customUkuranMap, brandName, tanggal]);

  // Execute Native Print
  const handlePrint = () => {
    if (stickersToPrint.length === 0) {
      toast.error('Pilih minimal satu varian untuk dicetak!');
      return;
    }

    const origTitle = document.title;
    document.title = `Stiker-Ruahan-${brandName || 'Sample'}`;

    toast.success(`Membuka dialog cetak untuk ${stickersToPrint.length} stiker ruahan...`, { duration: 1500 });
    
    setTimeout(() => {
      try {
        window.print();
      } finally {
        setTimeout(() => {
          document.title = origTitle;
        }, 500);
      }
    }, 150);
  };

  const isAllSelected = selectedIndices.size === variantList.length && variantList.length > 0;

  if (!isOpen) return null;

  return (
    <>
      {/* Global Print Styles (Only active while this modal is open) */}
      <style>{`
        @media screen {
          #ruahan-print-container {
            display: none !important;
          }
        }
        @media print {
          body > #root,
          [data-radix-portal],
          [role="dialog"] {
            display: none !important;
          }
          #ruahan-print-container {
            display: block !important;
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }
          @page {
            size: A4 portrait !important;
            margin: 0 !important;
          }
          html, body {
            background: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Main Interactive Dialog Modal */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden bg-white rounded-2xl border border-slate-200 shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/80 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#302621] text-white flex items-center justify-center shadow-xs">
                <Printer className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
                  <span>Cetak Label Sampel Ruahan</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 font-semibold">
                    40 x 20 mm
                  </span>
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Format lembar A4 otomatis persis seperti cetak sample
                </p>
              </div>
            </div>
          </div>

          {/* Modal Body: Full width for spacious editing */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 flex flex-col gap-4 min-h-0 bg-[#FCFBF9]">
            {/* Metadata Config */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200/80 space-y-3 shrink-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] font-bold text-slate-600 mb-1 flex items-center gap-1">
                    <Tag className="w-3 h-3 text-slate-400" /> Nama Brand
                  </Label>
                  <Input
                    type="text"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="Nama Brand..."
                    className="h-8 text-xs font-semibold bg-white border-slate-300"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-bold text-slate-600 mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-slate-400" /> Tanggal Label
                  </Label>
                  <Input
                    type="text"
                    value={tanggal}
                    onChange={(e) => setTanggal(e.target.value)}
                    placeholder="Contoh: 04 Oktober 2026"
                    className="h-8 text-xs font-medium bg-white border-slate-300"
                  />
                </div>
              </div>
            </div>

            {/* Variant List Table */}
            <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-2xs space-y-2.5 flex-1 flex flex-col min-h-0">
              <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 cursor-pointer"
                  >
                    {isAllSelected ? (
                      <CheckSquare className="w-4 h-4 text-[#8C6208]" />
                    ) : (
                      <Square className="w-4 h-4 text-slate-400" />
                    )}
                    <span>Pilih Semua ({variantList.length})</span>
                  </button>
                </div>

                <span className="text-xs text-slate-500 font-medium">
                  <strong className="text-slate-800">{selectedIndices.size}</strong> varian dipilih (Total <strong className="text-[#8C6208]">{stickersToPrint.length}</strong> stiker)
                </span>
              </div>

              {/* Table Header Bar */}
              <div className="hidden sm:flex items-center gap-3 px-3 py-1.5 bg-slate-100/70 rounded-lg text-[10px] font-black text-slate-500 uppercase tracking-wider border border-slate-200/60">
                <span className="w-5 shrink-0 text-center">✓</span>
                <span className="flex-1 min-w-[200px]">Nama Varian / Produk</span>
                <span className="w-28 shrink-0 text-center">Ukuran Sampel</span>
                <span className="w-28 shrink-0 text-center">Jumlah Stiker</span>
              </div>

              {/* Scrollable list */}
              <div className="flex-1 overflow-y-auto max-h-[380px] space-y-2 pr-1">
                {variantList.map((v) => {
                  const isChecked = selectedIndices.has(v.index);
                  const currentUkuran = customUkuranMap[v.index] !== undefined ? customUkuranMap[v.index] : v.defaultUkuran;
                  const currentCopies = copiesMap[v.index] || 1;

                  return (
                    <div
                      key={v.id}
                      onClick={() => handleToggleSelect(v.index)}
                      className={`p-3 rounded-xl border transition-all flex items-center justify-between gap-3 cursor-pointer ${
                        isChecked
                          ? 'bg-[#FAF7F2] border-[#8C6208]/40 shadow-xs'
                          : 'bg-white border-slate-200 opacity-60 hover:opacity-100'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => handleToggleSelect(v.index)}
                          className="shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-800 truncate">
                            {v.variantName}
                          </p>
                          <p className="text-[10px] text-slate-500 truncate">
                            Target order: {v.targetQty} pcs
                          </p>
                        </div>
                      </div>

                      {/* Ukuran input & Copies stepper */}
                      <div
                        className="flex items-center gap-3 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-28">
                          <Input
                            type="text"
                            value={currentUkuran}
                            onChange={(e) => handleUkuranChange(v.index, e.target.value)}
                            placeholder="30 ml"
                            className="h-8 text-xs font-semibold text-center bg-white border-slate-200 px-2"
                            title="Ukuran botol/sampel (misal: 30 ml)"
                          />
                        </div>

                        <div className="flex items-center gap-1 bg-white rounded-lg p-0.5 border border-slate-200 w-28 justify-between">
                          <button
                            type="button"
                            onClick={() => handleCopiesChange(v.index, currentCopies - 1)}
                            className="w-6 h-6 flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-100 rounded cursor-pointer"
                          >
                            -
                          </button>
                          <span className="text-xs font-bold text-slate-800">
                            {currentCopies}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopiesChange(v.index, currentCopies + 1)}
                            className="w-6 h-6 flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-100 rounded cursor-pointer"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-200 bg-white shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="text-xs font-semibold text-slate-600 hover:bg-slate-100"
            >
              Batal
            </Button>

            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-slate-600">
                Total: <strong className="text-slate-900 font-bold">{stickersToPrint.length} Stiker</strong>
              </span>

              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPreviewModal(true)}
                disabled={stickersToPrint.length === 0}
                className="border-[#D4AF37] bg-[#FAF7F2] hover:bg-[#F3ECE1] text-[#8C6208] text-xs font-bold px-4 h-9 rounded-xl shadow-xs gap-2 cursor-pointer transition-all disabled:opacity-50"
              >
                <Eye className="w-4 h-4 text-[#8C6208]" />
                <span>Preview Stiker ({stickersToPrint.length})</span>
              </Button>

              <Button
                type="button"
                onClick={handlePrint}
                disabled={stickersToPrint.length === 0}
                className="bg-[#302621] hover:bg-[#1A1411] text-[#D4AF37] border border-[#D4AF37]/40 text-xs font-bold px-5 h-9 rounded-xl shadow-md gap-2 cursor-pointer transition-all disabled:opacity-50"
              >
                <Printer className="w-4 h-4 text-[#D4AF37]" />
                <span>Cetak {stickersToPrint.length} Stiker Ruahan</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>      {/* Modal Preview Stiker Ruahan */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-7xl w-[98vw] max-h-[94vh] flex flex-col p-0 gap-0 overflow-hidden bg-white rounded-2xl border border-slate-200 shadow-2xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50 shrink-0 flex-wrap gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#302621] text-[#D4AF37] flex items-center justify-center font-bold">
                <Eye className="w-4 h-4" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-sm md:text-base font-bold text-slate-800">
                    Preview Label Sampel Ruahan (40 × 20 mm) — {stickersToPrint.length} Stiker
                  </DialogTitle>
                  <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#302621]/10 text-[#302621] border border-[#302621]/20">
                    5 Stiker Kesamping (Format Kertas A4)
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Format baku stiker 40x20mm (5 kolom per baris) pada lembar A4 dengan tag &quot;Ruahan&quot;
                </p>
              </div>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-white px-2.5 py-1 rounded-xl border border-slate-200 shadow-2xs">
              <button
                type="button"
                onClick={() => setPreviewZoom((z) => Math.max(0.7, Number((z - 0.1).toFixed(1))))}
                className="p-1 hover:text-slate-900 text-slate-500 cursor-pointer"
                title="Zoom Out"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs font-mono font-bold text-[#8C6208] px-1">
                {Math.round(previewZoom * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setPreviewZoom((z) => Math.min(1.8, Number((z + 0.1).toFixed(1))))}
                className="p-1 hover:text-slate-900 text-slate-500 cursor-pointer"
                title="Zoom In"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Preview Canvas - Responsif Mengikuti Ukuran Layar Masing-Masing */}
          <div className="flex-1 overflow-y-auto p-6 bg-slate-100 flex flex-wrap gap-4 items-center justify-center min-h-[320px]">
            {stickersToPrint.map((item, idx) => (
              <div key={`prev-ruahan-${item.id}-${idx}`} className="flex flex-col items-center gap-1.5 animate-in fade-in duration-100">
                <span className="text-[10px] font-bold text-slate-500 bg-white/80 px-2 py-0.5 rounded-full border border-slate-200 shadow-2xs self-start">
                  #{idx + 1} • {item.variantName}
                </span>
                <div
                  style={{
                    width: `${40 * previewZoom}mm`,
                    height: `${20 * previewZoom}mm`,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                  }}
                  className="bg-white rounded-none shrink-0 border border-slate-200"
                >
                  <RuahanLabelSticker
                    brandName={item.brandName}
                    variantName={item.variantName}
                    ukuran={item.ukuran}
                    tanggal={item.tanggal}
                    labelTag="Ruahan"
                    scale={previewZoom}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Preview Footer */}
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-200 bg-white shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPreviewModal(false)}
              className="text-xs font-semibold text-slate-600 hover:bg-slate-100"
            >
              Kembali ke Edit
            </Button>

            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">
                Total siap cetak: <strong className="text-slate-800">{stickersToPrint.length} Stiker</strong> (5 kolom pada kertas A4)
              </span>
              <Button
                type="button"
                onClick={() => {
                  setShowPreviewModal(false);
                  handlePrint();
                }}
                className="bg-[#302621] hover:bg-[#1A1411] text-[#D4AF37] border border-[#D4AF37]/40 text-xs font-bold px-5 h-9 rounded-xl shadow-md gap-2 cursor-pointer transition-all"
              >
                <Printer className="w-4 h-4 text-[#D4AF37]" />
                <span>Cetak Sekarang</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* A4 Print Sheet Container Portaled Directly to document.body (5 Kolom Kesamping) */}
      {isOpen &&
        stickersToPrint.length > 0 &&
        createPortal(
          <div id="ruahan-print-container">
            <div
              style={{
                width: '210mm',
                minHeight: '297mm',
                padding: '10mm 1mm',
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 40mm)',
                gridAutoRows: '20mm',
                justifyContent: 'center',
                alignContent: 'start',
                columnGap: '2mm',
                rowGap: '2mm',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
              }}
            >
              {stickersToPrint.map((item, index) => (
                <RuahanLabelSticker
                  key={`ruahan-stk-${item.id || index}`}
                  brandName={item.brandName}
                  variantName={item.variantName}
                  ukuran={item.ukuran}
                  tanggal={item.tanggal}
                  labelTag="Ruahan"
                />
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
